import {
  Checkbox,
  Flex,
  FlexItem,
  Form,
  FormGroup,
} from '@patternfly/react-core'
import * as React from 'react'
import { useCallback } from 'react'
import { connect, useDispatch } from 'react-redux'

import { FeatureFlagId } from '@app/domain/feature-flags/feature-flags.interface'
import type { RootState } from '@app/state'
import * as FeatureFlags from '@app/state/feature-flags'

import type { ConnectedProps} from 'react-redux';

const mapState = (state: RootState) => ({
  featureFlags: state.featureFlags,
})

const mapDispatch = () => {
  return {}
}

const connector = connect(mapState, mapDispatch)
type PropsFromRedux = ConnectedProps<typeof connector>

const FeatureFlagScreen = function ({ featureFlags }: PropsFromRedux) {
  const dispatch = useDispatch()

  const handleChange = useCallback(
    (checked, event) => {
      const target = event.target
      const value = target.type === 'checkbox' ? target.checked : target.value
      const id = target.id
      dispatch(FeatureFlags.actions.updateFeatureFlag(id, value))
    },
    [dispatch],
  )

  return (
    <Flex>
      <FlexItem>
        <Form isHorizontal>
          <FormGroup
            label="Program Facilitation"
            fieldId="program_facilitation"
          >
            <Checkbox
              id={FeatureFlagId.ProgramConfiguration}
              description="The ability to configure and view programs."
              key={FeatureFlagId.ProgramConfiguration}
              value={FeatureFlagId.ProgramConfiguration}
              label="Program Configuration"
              isChecked={featureFlags[FeatureFlagId.ProgramConfiguration]}
              onChange={handleChange}
            />
            <Checkbox
              id={FeatureFlagId.AddProgramToIntake}
              description="The ability to add program interactions to the intake workflow."
              key={FeatureFlagId.AddProgramToIntake}
              value={FeatureFlagId.AddProgramToIntake}
              label="Add program interactions to intake"
              isChecked={featureFlags[FeatureFlagId.AddProgramToIntake]}
              onChange={handleChange}
            />
            <Checkbox
              id={FeatureFlagId.InitialAndFollowUpInteraction}
              description="The ability to add initial and follow interactions to a program"
              key={FeatureFlagId.InitialAndFollowUpInteraction}
              value={FeatureFlagId.InitialAndFollowUpInteraction}
              label="Initial and FollowUp Interactions"
              isChecked={
                featureFlags[FeatureFlagId.InitialAndFollowUpInteraction]
              }
              onChange={handleChange}
            />
            <Checkbox
              id={FeatureFlagId.AdHocInteraction}
              description="The ability to add adhoc interactions to a program."
              key={FeatureFlagId.AdHocInteraction}
              value={FeatureFlagId.AdHocInteraction}
              label="AdHoc Interactions"
              isChecked={featureFlags[FeatureFlagId.AdHocInteraction]}
              onChange={handleChange}
            />
            <Checkbox
              id={FeatureFlagId.RemoteInteraction}
              description="The ability to add remote interactions to a program."
              key={FeatureFlagId.RemoteInteraction}
              value={FeatureFlagId.RemoteInteraction}
              label="Remote Interactions"
              isChecked={featureFlags[FeatureFlagId.RemoteInteraction]}
              onChange={handleChange}
            />
          </FormGroup>
          <FormGroup
            label="Site Administration"
            fieldId="site_administration"
            isInline={true}
          >
            <Checkbox
              id={FeatureFlagId.DataIntegrations}
              description="A feature flag that displays data integrations screen."
              key={FeatureFlagId.DataIntegrations}
              value={FeatureFlagId.DataIntegrations}
              label="Data Integrations"
              isChecked={featureFlags[FeatureFlagId.DataIntegrations]}
              onChange={handleChange}
            />
          </FormGroup>
          <FormGroup
            label="Test features"
            fieldId="test_features"
            isInline={true}
          >
            <Checkbox
              id={FeatureFlagId.FeatureFlagTest}
              description="A feature flag that displays text to validate the feature flag works."
              key={FeatureFlagId.FeatureFlagTest}
              value={FeatureFlagId.FeatureFlagTest}
              label="Test Feature"
              isChecked={featureFlags[FeatureFlagId.FeatureFlagTest]}
              onChange={handleChange}
            />
          </FormGroup>
          <FormGroup label="Workflow" fieldId="workflow" isInline={true}>
            <Checkbox
              id={FeatureFlagId.BypassPatientSignature}
              description="Bypass the patient's signature on the intake questionnaire."
              key={FeatureFlagId.BypassPatientSignature}
              value={FeatureFlagId.BypassPatientSignature}
              label="Bypass Patient Signature"
              isChecked={featureFlags[FeatureFlagId.BypassPatientSignature]}
              onChange={handleChange}
            />
          </FormGroup>
          <FormGroup label="Patient Management" fieldId="patients" isInline>
            <Checkbox
              id={FeatureFlagId.PatientMedicationHistory}
              description="The ability to view and edit a patient's medication history."
              key={FeatureFlagId.PatientMedicationHistory}
              value={FeatureFlagId.PatientMedicationHistory}
              label="Manage Patient Medication History"
              isChecked={featureFlags[FeatureFlagId.PatientMedicationHistory]}
              onChange={handleChange}
            />
          </FormGroup>
          <FormGroup label="Visit Management" fieldId="visits" isInline>
            <Checkbox
              id={FeatureFlagId.RestoreCancelledVisit}
              description="The ability for a user to restore a cancelled visit."
              key={FeatureFlagId.RestoreCancelledVisit}
              value={FeatureFlagId.RestoreCancelledVisit}
              label="Restore a cancelled visit."
              isChecked={featureFlags[FeatureFlagId.RestoreCancelledVisit]}
              onChange={handleChange}
            />
          </FormGroup>
        </Form>
      </FlexItem>
    </Flex>
  )
}

export default connector(FeatureFlagScreen)
